import { useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { Button } from '../atoms';
import { PageHeader } from '../shell';
import { DataTable, Pagination, ListToolbar, CreateEntityModal, ManageParentMembersModal } from '../organisms';
import { useServerList } from '../lib/useServerList';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * ParentClientsList — /v2/parent-clients
 *
 * The parent-company LIST view reached from the main menu. Bespoke (not the
 * generic list) so it can carry a per-row "Add clients" action — the reviewer
 * flagged (2026-07-24) that adding clients to a parent worked on the parent
 * PROFILE page but was missing here on the list. Each row also clicks through to
 * its roll-up (ParentClientRollup), and "+ New parent client" still creates one.
 *
 * Reuses the same searchable ManageParentMembersModal the profile page uses, so
 * membership is added identically from either surface.
 */

interface ParentRow {
  id: string;
  name: string;
  short_name: string | null;
  notes: string | null;
}

const truncate = (s: string | null, n = 80) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : (s ?? ''));

export function ParentClientsList() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  // The parent a per-row "Add clients" action is targeting (null = modal closed).
  const [addTarget, setAddTarget] = useState<{ id: string; name: string } | null>(null);
  // "+ New parent client" creates a parent (parent_client.can_add); the per-row
  // "Add clients" edits an existing parent's membership (parent_client.can_edit).
  const canAddParent = useEntityPermission('parent_client', 'can_add');
  const canEditParent = useEntityPermission('parent_client', 'can_edit');

  const list = useServerList<ParentRow>({
    resource: 'parent-clients',
    queryKey: ['parent-clients-list'],
    defaultSort: { id: 'name', desc: false },
  });
  const { rows, total, query: dataQuery } = list;

  const columns = useMemo<ColumnDef<ParentRow>[]>(() => {
    const cols: ColumnDef<ParentRow>[] = [
      { accessorKey: 'name', header: 'Name', cell: ({ getValue }) => (getValue() as string) || '—' },
      {
        accessorKey: 'short_name',
        header: 'Short name',
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return v ? v : <span style={{ color: 'var(--color-muted)' }}>—</span>;
        },
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        enableSorting: false,
        cell: ({ getValue }) => {
          const v = truncate(getValue() as string | null);
          return v ? v : <span style={{ color: 'var(--color-muted)' }}>—</span>;
        },
      },
    ];
    if (canEditParent) {
      cols.push({
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => { e.stopPropagation(); setAddTarget({ id: row.original.id, name: row.original.name }); }}
          >
            Add clients
          </Button>
        ),
      });
    }
    return cols;
  }, [canEditParent]);

  return (
    <>
      <PageHeader
        title="Parent Clients"
        subtitle={dataQuery.isLoading ? 'Loading…' : `${total.toLocaleString()} record${total === 1 ? '' : 's'}`}
        actions={
          canAddParent ? (
            <Button variant="primary" size="md" onClick={() => setCreateOpen(true)}>
              + New parent client
            </Button>
          ) : null
        }
      />

      <CreateEntityModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        table="parent_clients"
        routePath="parent-clients"
        invalidateKeys={[['parent-clients-list']]}
      />

      <ListToolbar
        searchValue={list.searchInput}
        onSearchChange={list.setSearchInput}
        searchPlaceholder="Search parent clients…"
      />

      {(total > 0 || dataQuery.isFetching) && (
        <>
          <DataTable
            data={rows}
            columns={columns}
            sorting={list.sorting}
            onSortingChange={list.setSorting}
            onRowClick={(row) => navigate(`/parent-clients/${row.id}`)}
            emptyMessage={dataQuery.isFetching ? 'Loading…' : 'No records'}
          />
          <Pagination
            pageIndex={list.pageIndex}
            pageCount={list.pageCount}
            pageSize={list.pageSize}
            totalRows={total}
            onPageChange={list.setPageIndex}
            onPageSizeChange={list.setPageSize}
          />
        </>
      )}
      {!dataQuery.isLoading && total === 0 && !dataQuery.isFetching && (
        <div style={{
          padding: 'var(--sp-6)', background: 'var(--color-card)', border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-lg)', color: 'var(--color-muted)', textAlign: 'center', fontSize: 'var(--fs-md)',
        }}>
          {list.search ? 'No parent clients match that search.' : 'No parent clients yet. Use “+ New parent client” to create one.'}
        </div>
      )}

      {addTarget && (
        <ManageParentMembersModal
          parentId={addTarget.id}
          parentName={addTarget.name}
          open
          onClose={() => setAddTarget(null)}
        />
      )}
    </>
  );
}
